import React, {useContext, useState, useEffect} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import {withRouter} from 'react-router';
import {AuthContext} from '../../../context/auth';
import {useLocation} from 'react-router-dom';
import {PRODUCT_KEY, FEATURE_KEY, DQ_PRO_PLAN_KEY} from '../../../utils/constants';
import { isProductEnabled, canViewProduct, canCreateProduct, isFeatureEnabled } from '../../../utils/productAccess';
import { canAuthorLearningProgram } from '../../../utils/capabilities';
import WorkspaceNav from './WorkspaceNav.comp';
import { SCHOOL_WORKSPACE_NAV } from '../../../config/workspaceNav/schoolWorkspace.nav';
import { NETWORK_WORKSPACE_NAV } from '../../../config/workspaceNav/networkWorkspace.nav';
import { resolveSolutions } from '../../../config/workspaceNav/solutions';
import { listOrgClassesService } from '../../../services/client.service';
import { useTutorial } from '../../../components/TutorialSystem';

const ORANGE = 'var(--brand-primary, #FF6900)';
const GREY = '#A5A5A5';

const useStyles = makeStyles(() => ({
    item: {
        width: '92%',
        margin: '2px 10px',
        borderRadius: '10px',
        padding: '10px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    itemCollapsed: {
        width: '65%',
    },
    itemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    /*
     * Levels 2 and 3 are separated by indent AND type weight — the same
     * treatment as the Partner sidebar, so the two workspaces read identically.
     */
    subItem: {
        width: 'calc(100% - 34px)',
        margin: '1px 10px 1px 24px',
        borderRadius: '8px',
        padding: '7px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    // Level 3 — Solutions > COS Children Assessment > Preview.
    subItem2: {
        width: 'calc(100% - 48px)',
        margin: '1px 10px 1px 38px',
        borderRadius: '8px',
        padding: '5px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    subItemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    icon: {minWidth: 'unset', marginRight: '12px'},
    label: {
        '& .MuiTypography-root': {fontSize: '14px', fontWeight: 600, color: 'inherit'},
    },
    labelSub: {
        '& .MuiTypography-root': {fontSize: '13.5px', fontWeight: 500, color: 'inherit'},
    },
    labelSub2: {
        '& .MuiTypography-root': {fontSize: '13px', fontWeight: 400, color: 'inherit'},
    },
    divider: {margin: '4px 10px'},
}));

/**
 * Sidebar for a level-22 school.
 *
 * Rendered from config via the shared WorkspaceNav, exactly as the Partner
 * sidebar is — the sheet gives School the same three-level shape, and one
 * renderer means an expander bug is fixed once rather than per workspace.
 *
 * Every gate below is carried over unchanged from the hand-written version this
 * replaces. The sheet restructures the menu; it does not grant new access.
 */
const PartialMainList = (props) => {
    const {history, isDrawerOpen} = props;
    const classes = useStyles();
    const location = useLocation();
    const {authUser} = useContext(AuthContext);

    const { isActive: isTutorialActive, currentStep: tutorialStep } = useTutorial();

    /**
     * The tutorial points at sub-items inside My Programs. A highlighted element
     * inside a collapsed group cannot be seen, so the group is forced open for
     * the duration of those steps.
     */
    const programSubTargets = ['#sidebar-products-link'];
    const forceOpenCodes = isTutorialActive && tutorialStep && programSubTargets.includes(tutorialStep.target)
        ? ['SW_MP', 'SNW_MP']
        : [];

    /**
     * P1 Workspace flow: a school under a P1 owner must be able to see the learning
     * programs its DQ Partner delivered. Product access is resolved per-user and the
     * school carries no learning_program flag of its own (the owner holds it), so
     * without this the item stays hidden on the very plan built around it. VIEW only —
     * "Create" below still requires the create permission.
     */
    const isP1School = authUser?.dq_pro_plan?.plan_key === DQ_PRO_PLAN_KEY.ENTERPRISE_P1;

    const hasLearningPrograms = isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM) || isP1School;
    const hasAssessments = isProductEnabled(authUser, PRODUCT_KEY.ASSESSMENT)
        || isProductEnabled(authUser, PRODUCT_KEY.CPC_INDEX_ASSESSMENT);
    // The "Learning Programs" list item follows the learning_program `view` permission.
    const canViewLearningPrograms = canViewProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM) || isP1School;
    // The "Create" item follows the learning_program `create` permission AND the
    // per-user capability — the same pair the server requires (productGuard +
    // capabilityGuard). Showing it on the product half alone offered authoring to
    // a school lead who would be refused on submit.
    const canCreateLearningPrograms = canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM)
        && canAuthorLearningProgram(authUser);
    // Parent shows only when at least one child is visible (avoids an empty "My Programs").
    const hasAnyProgramAccess = (hasLearningPrograms && canViewLearningPrograms) || hasAssessments;
    // A network's PAC feature is its own key, school_network_dashboard —
    // distinct from a single school's school_dashboard — and it lands on its
    // own dashboard route, not the single-school dashboard (see
    // networkWorkspace.nav.js).
    const insightsPath = authUser?.is_school_network
        ? (isFeatureEnabled(authUser, FEATURE_KEY.SCHOOL_NETWORK_DASHBOARD)
            ? '/dq_pro/network-dashboard/overview'
            : '/dq_pro/insights')
        : (isFeatureEnabled(authUser, FEATURE_KEY.SCHOOL_DASHBOARD)
            ? '/dq_pro/school-dashboard/overview'
            : '/dq_pro/insights');

    const isActive = path => location.pathname === path || location.pathname.startsWith(path + '/');

    /**
     * Classes for the Insights > Class Dashboard entries.
     *
     * The sheet gives that row one entry PER CLASS ("Class 1, 2, 3, ..."),
     * and each class dashboard is scoped by section_code — so the menu needs
     * the real list. Fetched here because the sidebar is the only place that
     * renders it.
     *
     * GUARDED, because this is the one network call the sidebar makes. It runs
     * only for a workspace that has an org_code AND the school dashboard
     * feature; for anyone else the row is not rendered at all, so fetching
     * would be pure cost. Failure resolves to [] — a sidebar that cannot load
     * its classes renders without them rather than breaking.
     */
    const [classRows, setClassRows] = useState([]);
    const canSeeClassDashboards = isFeatureEnabled(authUser, FEATURE_KEY.CLASS_DASHBOARD);
    useEffect(() => {
        let cancelled = false;
        if (!authUser?.org_code || !canSeeClassDashboards) {
            setClassRows([]);
            return undefined;
        }
        listOrgClassesService(authUser.org_code)
            .then((rows) => { if (!cancelled) setClassRows(Array.isArray(rows) ? rows : []); })
            .catch(() => { if (!cancelled) setClassRows([]); });
        return () => { cancelled = true; };
    }, [authUser?.org_code, canSeeClassDashboards]);

    /** Everything the nav config's `visibleWhen` and `path` resolvers read. */
    const navCtx = {
        authUser,
        insightsPath,
        hasLearningPrograms,
        hasAssessments,
        canViewLearningPrograms,
        canCreateLearningPrograms,
        hasAnyProgramAccess,
        // Consumed by schoolWorkspace.nav.js — SW_IN_CD builds one child per class.
        classRows,
    };

    /**
     * Unlocked solutions, resolved from the session on the client. Not yet used
     * as a gate — see SOLUTION_GATING_READY.
     */
    const solutions = resolveSolutions(authUser);

    /**
     * School vs. School Network.
     *
     * Both are level 22; the difference is a flag on the organisation, which
     * the session carries as `is_school_network` (see _attachWorkspaceShapeToUser in
     * auth.dqin.service.js). A network gets the SNW menu from the Navigation
     * Bar sheet rows 48-100 — the same rows plus "My Schools".
     */
    const navItems = authUser?.is_school_network ? NETWORK_WORKSPACE_NAV : SCHOOL_WORKSPACE_NAV;

    return (
        <div style={{display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden'}}>
            <List style={{flexGrow: 1, paddingTop: '8px', overflowY: 'auto'}}>
                {/*
                  * The whole menu comes from the PbDQ Navigation Bar sheet rows
                  * 105-147, via config rather than JSX. Adding or moving an item
                  * is a row in schoolWorkspace.nav.js.
                  */}
                <WorkspaceNav
                    items={navItems}
                    ctx={navCtx}
                    solutions={solutions}
                    classes={classes}
                    isDrawerOpen={isDrawerOpen}
                    currentPath={location.pathname}
                    isActive={isActive}
                    onNavigate={(path) => history.push(path)}
                    forceOpenCodes={forceOpenCodes}
                />
            </List>
        </div>
    );
}

export default withRouter(PartialMainList);
